import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Phone, Mail, LogIn, Shield } from 'lucide-react';
import { Button } from '../ui/button';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle login click with returnTo preservation
  const handleLoginClick = (e) => {
    e.preventDefault();
    const currentPath = location.pathname;
    if (currentPath.startsWith('/app/') || 
        currentPath.startsWith('/portal/') || 
        currentPath.startsWith('/admin/') ||
        currentPath.startsWith('/inkflo/')) {
      sessionStorage.setItem('auth_redirect', currentPath + location.search);
    }
    navigate('/login');
  };

  // Kaiya Platform - Primary Protection Layers
  const platformLinks = [
    { name: 'VendorGuard', href: '/vendorguard' },
    { name: 'GrantGuard', href: '/grantguard' },
    { name: 'CouncilGuard', href: '/councilguard' },
    { name: 'PROOF', href: '/proof' },
    { name: 'ManifestGuard', href: '/manifestguard' },
    { name: 'ECom Protect', href: '/ecom-protect' },
  ];

  // Resources
  const resourceLinks = [
    { name: 'Evidence Ledger', href: '/evidence-ledger' },
    { name: 'Risk Intelligence', href: '/risk-intelligence' },
    { name: 'How Kaiya Works', href: '/how-kaiya-works' },
    { name: 'Security', href: '/security' },
    { name: 'Certified Operators Academy', href: '/academy' },
  ];

  // Tools - Single link to Communications
  const toolsLinks = [
    { name: 'Communications', href: '/communications' },
  ];

  // Legal Links
  const legalLinks = [
    { name: 'Terms of Service', href: '/legal#terms' },
    { name: 'Privacy Policy', href: '/legal#privacy' },
    { name: 'Risk Disclosure', href: '/legal#risk-disclosure' },
  ];

  return (
    <footer className="bg-[#0a1929] border-t border-[#48a8ab]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        {/* Brand Section */}
        <div className="mb-12">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            <div className="max-w-md">
              <Link to="/" className="flex items-center gap-2 mb-4" data-testid="footer-logo-link">
                <img 
                  src="/kaiya-logo.png" 
                  alt="Kaiya Solutions" 
                  className="h-12 w-auto object-contain"
                  data-testid="footer-logo"
                />
              </Link>
              <p className="text-[#48a8ab] font-medium mb-2">
                Risk Infrastructure and Operational Evidence Systems
              </p>
              <p className="text-gray-400 text-sm">
                Documentation. Verification. Audit Defensibility.
              </p>
            </div>
            
            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:775-722-4569" 
                className="flex items-center gap-2 text-gray-400 hover:text-[#48a8ab] transition-colors duration-200"
              >
                <Phone className="h-4 w-4" />
                <span>775-722-4569</span>
              </a>
              <a href="mailto:kyto@kaiyasolutions.com">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-[#48a8ab] text-[#48a8ab] hover:bg-[#48a8ab] hover:text-white"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email Us
                </Button>
              </a>
              <button
                onClick={handleLoginClick}
                className="flex items-center gap-2 text-gray-400 hover:text-[#48a8ab] transition-colors duration-200"
              >
                <LogIn className="h-4 w-4" />
                <span>Sign In</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Link Grid - 4 Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Kaiya Platform */}
          <div>
            <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#48a8ab]" />
              Kaiya Platform
            </h4>
            <ul className="space-y-3">
              {platformLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#48a8ab] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Resources</h4>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#48a8ab] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Tools - Single Communications Link */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Tools</h4>
            <ul className="space-y-3">
              {toolsLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#48a8ab] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Legal</h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#48a8ab] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Kaiya Solutions. All rights reserved.
            </p>
            <p className="text-gray-600 text-xs text-center md:text-right max-w-md">
              Kaiya Solutions is a Risk Infrastructure Provider. We are not a law firm, 
              accounting firm, auditing firm, or insurance carrier.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
